import React from 'react';
import { FiBellOff } from 'react-icons/fi';
import { BsCheck, BsCheckAll } from 'react-icons/bs';
import Avatar from '../Shared/Avatar';
import { useI18n } from '../../../i18n/I18nContext';
import { useUserPresence } from '../../../context/TalkPresenceContext';



/**
 * ConversationItem Component
 * Displays a single conversation in the sidebar list
 *
 * @param {Object} props
 * @param {Object} props.conversation - Conversation data
 * @param {boolean} props.isActive - Whether this conversation is currently active
 * @param {function} props.onClick - Handler for clicking the conversation
 * @param {Object} props.currentUser - Current user (to determine "You:" prefix)
 */
const ConversationItem = ({
  conversation,
  isActive = false,
  onClick,
  currentUser
}) => {
  const { t } = useI18n();
  const peer = conversation.type === 'direct'
    ? conversation.users?.find(user => String(user.id) !== String(currentUser?.id)) : null;
  const presence = useUserPresence(peer?.id, peer?.last_seen_at);
  // Helper functions
  const getDisplayName = () => {
    if (conversation.type === 'team' && conversation.context?.name) {
      return conversation.context.name;
    }

    if (conversation.type === 'group') {
      const otherUsers = conversation.users?.filter(u => u.id !== currentUser?.id) || [];
      return otherUsers.map(u => u.name).join(', ') || 'Group Chat';
    }

    // For direct conversations, show the other user's name
    const otherUser = conversation.users?.find(u => u.id !== currentUser?.id);
    return otherUser?.name || conversation.name || 'Chat';
  };

  const getAvatar = () => {
    if (conversation.type === 'team') {
      return {
        src: conversation.context?.avatar,
        name: getDisplayName()
      };
    }

    if (conversation.type === 'group') {
      return {
        src: null,
        name: getDisplayName()
      };
    }

    const otherUser = conversation.users?.find(u => u.id !== currentUser?.id);
    return {
      src: otherUser?.avatar,
      name: otherUser?.name || 'User'
    };
  };

  const getLastMessagePreview = () => {
    const lastMsg = conversation.last_message;

    if (!lastMsg) {
      return 'No messages yet';
    }
    if (lastMsg.deleted_at) return t('talk.deletion.deleted', {}, 'This message was deleted');

    let prefix = '';
    if (conversation.type === 'team' && lastMsg.sender) {
      if (lastMsg.sender.id === currentUser?.id) {
        prefix = 'You: ';
      } else {
        prefix = `${lastMsg.sender.name?.split(' ')[0]}: `;
      }
    } else if (lastMsg.sender?.id === currentUser?.id) {
      prefix = 'You: ';
    }

    // Handle different message types
    if (lastMsg.kind === 'attachment') {
      return `${prefix}📎 Attachment`;
    }

    return `${prefix}${lastMsg.body || ''}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    // Today: show time
    if (diffInHours < 24 && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      });
    }

    // Yesterday
    if (diffInDays < 2 && date.getDate() === now.getDate() - 1) {
      return 'Yesterday';
    }

    // This week: show day name
    if (diffInDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }

    // Older: show date
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? '2-digit' : undefined
    });
  };

  const getReadReceipt = () => {
    const lastMsg = conversation.last_message;

    // Only show read receipts for messages sent by current user
    if (!lastMsg || lastMsg.sender?.id !== currentUser?.id) {
      return null;
    }

    // TODO: Implement actual read receipt logic based on backend data
    // For now, showing checkmarks based on message state
    const isRead = false; // Replace with actual logic
    const isDelivered = true; // Replace with actual logic

    if (isRead) {
      return <BsCheckAll className="read-receipt read-receipt-read" size={16} />;
    }

    if (isDelivered) {
      return <BsCheckAll className="read-receipt" size={16} />;
    }

    return <BsCheck className="read-receipt" size={16} />;
  };

  const avatar = getAvatar();
  const unreadCount = conversation.unread_count || 0;
  const isMuted = conversation.muted_until && new Date(conversation.muted_until) > new Date();
  const hasUnread = unreadCount > 0;

  return (
    <div
      className={`talk-conversation-item ${isActive ? 'talk-conversation-active' : ''} ${hasUnread ? 'talk-conversation-unread' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`${getDisplayName()}, ${hasUnread ? `${unreadCount} unread messages` : 'no unread messages'}`}
    >
      <Avatar
        src={avatar.src}
        name={avatar.name}
        size={54}
        isOnline={Boolean(peer && presence.is_online)}
        status={peer && presence.is_online ? 'online' : 'offline'}
      />

      <div className="talk-conversation-content">
        <div className="talk-conversation-top">
          <div className="talk-conversation-name">
            {getDisplayName()}
          </div>

          <div className="talk-conversation-meta">
            {isMuted && (
              <FiBellOff
                className="talk-conversation-mute-icon"
                size={14}
                aria-label="Muted"
              />
            )}
            <span className="talk-conversation-time">
              {formatTimestamp(conversation.last_message_at)}
            </span>
          </div>
        </div>

        <div className="talk-conversation-bottom">
          <div className="talk-conversation-preview">
            {getReadReceipt()}
            <span className="talk-conversation-preview-text">
              {getLastMessagePreview()}
            </span>
          </div>

          {hasUnread && (
            <span
              className={`talk-conversation-badge ${isMuted ? 'talk-conversation-badge-muted' : ''}`}
              aria-label={`${unreadCount} unread`}
            >
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationItem;
